/**
 * SdpBitrateTransform - Injects video bandwidth lines and H.264 x-google-* hints.
 *
 * For every video m-section (H.264, HEVC, AV1, VP8/VP9, …), injects `b=TIAS`
 * (bps) and `b=AS` (kbps) after the `c=` line when missing — codec-agnostic.
 *
 * Codec-specific fmtp bitrate keys (`x-google-*-bitrate`, sps-pps-idr) are
 * applied only to H.264, where Chromium accepts them. HEVC/AV1/VP8/VP9 fmtp
 * lines are left unchanged.
 */

export type SdpBitrateConfig = {
  /** Maximum bitrate in kbps (e.g. 10000 for 10 Mbit). */
  maxBitrate: number;
  /** Minimum bitrate in kbps (e.g. 3500 for 3.5 Mbit). */
  minBitrate: number;
  /** Initial bitrate in kbps (e.g. 7000 for 7 Mbit). */
  startBitrate: number;
  /** Opus maxaveragebitrate in bps (optional). */
  audioMaxBitrate?: number;
  /** When false, Opus fmtp uses mono stereo/sprop-stereo flags. */
  audioStereo?: boolean;
  /**
   * Rewrite the audio section (Opus fmtp + 20 ms ptime). Part of the Chromium low-latency
   * path; off leaves the audio section exactly as the browser produced it (the pre-2026-09-25
   * behaviour every non-Chromium guest still gets).
   */
  audioOpusPolicy?: boolean;
};

/**
 * Collect all payload type numbers mapped to H.264 in the SDP.
 * Scans `a=rtpmap:<PT> H264/90000` lines.
 */
function findH264PayloadTypes(sdp: string): Set<string> {
  const payloadTypes = new Set<string>();
  const lines = sdp.split('\r\n');
  for (const line of lines) {
    if (line.startsWith('a=rtpmap:') && line.includes('H264/90000')) {
      const pt = line.substring('a=rtpmap:'.length).split(' ')[0];
      if (pt) payloadTypes.add(pt);
    }
  }
  return payloadTypes;
}

/** fmtp parameters this transform owns — any inbound copy is dropped, then re-added. */
const OWNED_FMTP_PARAMS = new Set([
  'sps-pps-idr-in-keyframe',
  'x-google-max-bitrate',
  'x-google-min-bitrate',
  'x-google-start-bitrate',
]);

const OWNED_OPUS_FMTP_PARAMS = new Set([
  'useinbandfec',
  'usedtx',
  'stereo',
  'sprop-stereo',
  'maxaveragebitrate',
]);

const OPUS_PTIME_LINE = 'a=ptime:20';

function fmtpParamName(param: string): string {
  const eq = param.indexOf('=');
  return (eq === -1 ? param : param.substring(0, eq)).trim().toLowerCase();
}

/**
 * Strip every parameter this transform owns from an fmtp value, then append ours.
 *
 * Matching on the parameter *name* after splitting on `;` — rather than replacing a
 * value-bearing pattern — is what keeps this correct in two ways a regex got wrong:
 * an inbound value we do not expect (`sps-pps-idr-in-keyframe=0`) is still removed
 * instead of surviving alongside our own copy with a conflicting value, and a name can
 * never match as a substring inside some longer parameter name.
 */
function rewriteFmtpParams(existingParams: string, config: SdpBitrateConfig): string {
  const preserved = existingParams
    .split(';')
    .map((param) => param.trim())
    .filter((param) => param.length > 0 && !OWNED_FMTP_PARAMS.has(fmtpParamName(param)));

  return [
    ...preserved,
    'sps-pps-idr-in-keyframe=1',
    `x-google-max-bitrate=${config.maxBitrate}`,
    `x-google-min-bitrate=${config.minBitrate}`,
    `x-google-start-bitrate=${config.startBitrate}`,
  ].join(';');
}

/**
 * Inject `b=TIAS:<bps>` and `b=AS:<kbps>` after each video m-section `c=` line.
 * If AS exists without TIAS, inserts TIAS (does not no-op on "any b=").
 */
function injectVideoBandwidthLines(sdp: string, maxBitrateKbps: number): string {
  if (!Number.isFinite(maxBitrateKbps) || maxBitrateKbps <= 0) return sdp;

  const lines = sdp.split('\r\n');
  const result: string[] = [];
  let insideVideo = false;
  const maxBitrateBps = Math.max(1, Math.round(maxBitrateKbps * 1000));
  const asKbps = Math.max(1, Math.round(maxBitrateKbps));
  const tiasLine = `b=TIAS:${maxBitrateBps}`;
  const asLine = `b=AS:${asKbps}`;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('m=')) {
      insideVideo = line.startsWith('m=video');
      result.push(line);
      continue;
    }
    if (insideVideo && line.startsWith('c=')) {
      result.push(line);
      // Collect existing b= lines immediately following c=
      const bandwidthLines: string[] = [];
      let j = i + 1;
      while (j < lines.length && lines[j].startsWith('b=')) {
        bandwidthLines.push(lines[j]);
        j++;
      }
      const hasTias = bandwidthLines.some((b) => b.startsWith('b=TIAS:'));
      const hasAs = bandwidthLines.some((b) => b.startsWith('b=AS:'));
      if (!hasTias) result.push(tiasLine);
      if (!hasAs) result.push(asLine);
      // Skip the original b= lines in the outer loop by not pushing them here —
      // they will be pushed when the loop advances. We only inject missing ones.
      continue;
    }
    result.push(line);
  }

  return result.join('\r\n');
}

/**
 * Rewrite H.264 a=fmtp lines with x-google-* bitrate hints.
 */
function splitSdpLines(sdp: string): { eol: '\r\n' | '\n'; lines: string[] } {
  if (sdp.includes('\r\n')) {
    return { eol: '\r\n', lines: sdp.split('\r\n') };
  }
  return { eol: '\n', lines: sdp.split('\n') };
}

function findOpusPayloadTypesInSection(lines: string[], start: number, end: number): Set<string> {
  const payloadTypes = new Set<string>();
  for (let i = start; i < end; i++) {
    const line = lines[i];
    if (!line.startsWith('a=rtpmap:')) continue;
    if (!/opus\/48000/i.test(line)) continue;
    const pt = line.substring('a=rtpmap:'.length).split(' ')[0];
    if (pt) payloadTypes.add(pt);
  }
  return payloadTypes;
}

function rewriteOpusFmtpParams(existingParams: string, config: SdpBitrateConfig): string {
  const preserved = existingParams
    .split(';')
    .map((param) => param.trim())
    .filter((param) => param.length > 0 && !OWNED_OPUS_FMTP_PARAMS.has(fmtpParamName(param)));

  const stereo = config.audioStereo !== false;
  // Inband FEC makes NetEq hold an extra frame for a redundant packet. Conceal a lost
  // 20 ms frame instead of waiting for it.
  const owned = ['useinbandfec=0', 'usedtx=0'];
  if (stereo) {
    owned.push('stereo=1', 'sprop-stereo=1');
  } else {
    owned.push('stereo=0', 'sprop-stereo=0');
  }
  const maxBps = config.audioMaxBitrate;
  if (maxBps !== undefined && Number.isFinite(maxBps) && maxBps > 0) {
    owned.push(`maxaveragebitrate=${Math.round(maxBps)}`);
  }

  return [...preserved, ...owned].join(';');
}

/**
 * Enforce Opus fmtp (no inband FEC, no DTX) and 20 ms packetization in each audio m-section.
 */
function injectAudioOpusPolicy(sdp: string, config: SdpBitrateConfig): string {
  if (!sdp) return sdp;

  const { eol, lines } = splitSdpLines(sdp);
  const result: string[] = [];

  for (let i = 0; i < lines.length; ) {
    const line = lines[i];
    if (!line.startsWith('m=audio')) {
      result.push(line);
      i++;
      continue;
    }

    let sectionEnd = i + 1;
    while (sectionEnd < lines.length && !lines[sectionEnd].startsWith('m=')) {
      sectionEnd++;
    }

    const opusPts = findOpusPayloadTypesInSection(lines, i, sectionEnd);
    let ptimeWritten = false;

    for (let j = i; j < sectionEnd; j++) {
      const sectionLine = lines[j];
      if (sectionLine.startsWith('a=ptime:')) {
        result.push(OPUS_PTIME_LINE);
        ptimeWritten = true;
        continue;
      }

      if (sectionLine.startsWith('a=fmtp:') && opusPts.size > 0) {
        const spaceIdx = sectionLine.indexOf(' ');
        if (spaceIdx === -1) {
          result.push(sectionLine);
          continue;
        }
        const pt = sectionLine.substring('a=fmtp:'.length, spaceIdx);
        if (!opusPts.has(pt)) {
          result.push(sectionLine);
          continue;
        }
        const existingParams = sectionLine.substring(spaceIdx + 1);
        result.push(`a=fmtp:${pt} ${rewriteOpusFmtpParams(existingParams, config)}`);
        continue;
      }

      result.push(sectionLine);
    }

    if (opusPts.size > 0 && !ptimeWritten) {
      result.push(OPUS_PTIME_LINE);
    }

    i = sectionEnd;
  }

  return result.join(eol);
}

function injectH264FmtpBitrateHints(sdp: string, config: SdpBitrateConfig): string {
  const h264PTs = findH264PayloadTypes(sdp);
  if (h264PTs.size === 0) return sdp;

  const lines = sdp.split('\r\n');
  const result: string[] = [];

  for (const line of lines) {
    if (!line.startsWith('a=fmtp:')) {
      result.push(line);
      continue;
    }

    const spaceIdx = line.indexOf(' ');
    if (spaceIdx === -1) {
      result.push(line);
      continue;
    }

    const pt = line.substring('a=fmtp:'.length, spaceIdx);
    if (!h264PTs.has(pt)) {
      result.push(line);
      continue;
    }

    const existingParams = line.substring(spaceIdx + 1);
    const newParams = rewriteFmtpParams(existingParams, config);
    result.push(`a=fmtp:${pt} ${newParams}`);
  }

  return result.join('\r\n');
}

/**
 * Returns an sdpTransform function suitable for PeerConnectionOptions.sdpTransform.
 *
 * Injects video `b=TIAS`/`b=AS` bandwidth lines and H.264 fmtp
 * x-google-max/min/start-bitrate plus sps-pps-idr-in-keyframe=1.
 */
export function createSdpBitrateTransform(config: SdpBitrateConfig): (sdp: string) => string {
  return (sdp: string): string => {
    if (!sdp) return sdp;

    // Default ON: this rewrite is part of the Chromium path that was already shipping.
    // Safari passes audioOpusPolicy: false and keeps the audio section the browser wrote.
    const withAudio = config.audioOpusPolicy === false ? sdp : injectAudioOpusPolicy(sdp, config);
    const withBandwidth = injectVideoBandwidthLines(withAudio, config.maxBitrate);
    return injectH264FmtpBitrateHints(withBandwidth, config);
  };
}
